import React from 'react';

import { useReducedMotion } from '../../hooks/useReducedMotion';
import { MAILBOX_PROVISION_ANIM } from '../../theme/tokens';
import { PROVISION_ROWS } from './provisioning';

type Paced = {
  address: string | null;
  step: number;
  /** The last row has had its full ROW_MS: it completes as soon as the create has answered. */
  lastDue: boolean;
};

/**
 * The step index `ProvisioningView` renders from (rows below it done, the row at it in progress), for
 * the mailbox at `address` (`null` when no mailbox is being set up).
 *
 * The screen appears on the click, with the create still in flight, so the step starts at 0 (row 1
 * in progress) and walks one row per `MAILBOX_PROVISION_ANIM.ROW_MS`, every row the same length.
 * Only the LAST completion waits for anything: it needs its ROW_MS AND the create's answer
 * (`answered`), so a create slower than the run holds the last row's spinner, never a middle one.
 * Nothing here waits on DNS (see `provisioning.ts`). It never goes back, it is keyed by the address
 * so another mailbox starts afresh, and it holds where it is while `paused` (a failure is showing),
 * resuming from that row once the failure clears. Under reduced motion it jumps to the last row and
 * completes it on the answer.
 */
export const useProvisioningStep = (address: string | null, paused: boolean, answered: boolean): number => {
  const reduced = useReducedMotion();
  const last = PROVISION_ROWS - 1;

  // The step shown for this address. Never decreases. Adjusted while rendering for a new address, for
  // reduced motion (no timer to wait for), and for the last row once it is due and the create answered.
  const [paced, setPaced] = React.useState<Paced>(() => ({ address, step: 0, lastDue: false }));
  const current: Paced = paced.address === address ? paced : { address, step: 0, lastDue: false };
  if (paced.address !== address) setPaced(current);
  else if (reduced && !paused && (current.step < last || !current.lastDue)) {
    setPaced({ address, step: last, lastDue: true });
  } else if (!paused && answered && current.step === last && current.lastDue) {
    setPaced({ address, step: PROVISION_ROWS, lastDue: true });
  }

  // The pacing timer: one row per ROW_MS; at the last row it only marks the row due (the answer
  // completes it). Synchronises with the clock.
  const { step, lastDue } = current;
  React.useEffect(() => {
    if (address === null || reduced || paused || step >= PROVISION_ROWS || (step === last && lastDue)) {
      return undefined;
    }
    const id = window.setTimeout(
      () => setPaced(step < last ? { address, step: step + 1, lastDue: false } : { address, step, lastDue: true }),
      MAILBOX_PROVISION_ANIM.ROW_MS,
    );
    return () => window.clearTimeout(id);
  }, [address, reduced, paused, step, lastDue, last]);

  return address === null ? 0 : step;
};

export default useProvisioningStep;
